import React, { createContext, useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
    getAuth, 
    signInWithPopup, 
    GoogleAuthProvider, 
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail
} from 'firebase/auth';
import { db } from '../services/database';
import { OAuthProvider } from 'firebase/auth';

// Firebase configuration
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "your-api-key",
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "your-project.firebaseapp.com",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "your-project-id",
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "your-project.appspot.com",
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "123456789",
    appId: process.env.REACT_APP_FIREBASE_APP_ID || "your-app-id"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();
const microsoftProvider = new OAuthProvider('microsoft.com');

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    const dbUser = await db.syncUser({
                        firebase_uid: firebaseUser.uid,
                        email: firebaseUser.email,
                        display_name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
                        phone: null
                    });
                
                    // User logged in
                    const userData = {
                        id: dbUser.id,
                        uid: firebaseUser.uid,
                        name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
                        email: firebaseUser.email,
                        phone: dbUser.phone,
                        profilePicture: firebaseUser.photoURL,
                        authProvider: firebaseUser.providerData[0]?.providerId.includes('google') 
                            ? 'google'
                            : firebaseUser.providerData[0]?.providerId.includes('microsoft')
                            ? 'microsoft'
                            : 'email'
                    };
                
                    setUser(userData);
                } catch (error) {
                    console.error('Failed to sync user with database:', error);
                    const userData = {
                        uid: firebaseUser.uid,
                        name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
                        email: firebaseUser.email,
                        profilePicture: firebaseUser.photoURL,
                        authProvider: firebaseUser.providerData[0]?.providerId.includes('google') ? 'google' : 'email'
                    };  
                    setUser(userData);                  
                }
            } else {
                // User logged out
                setUser(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const loginWithEmail = async (email, password) => {
        try {
            setLoading(true);
            await signInWithEmailAndPassword(auth, email, password);
            return { success: true };
        } catch (error) {
            console.error('Email login failed:', error);
            return { 
                success: false, 
                error: getAuthErrorMessage(error.code)
            };
        } finally {
            setLoading(false);
        }
    };

    const registerWithEmail = async (userData) => {
        try {
            setLoading(true);
            const userCredential = await createUserWithEmailAndPassword(
                auth, 
                userData.email, 
                userData.password
            );

            await userCredential.user.updateProfile({
                displayName: `${userData.firstName} ${userData.lastName}`
            });

            return { success: true };
        } catch (error) {
            console.error('Registration failed:', error);
            return { 
                success: false, 
                error: getAuthErrorMessage(error.code)
            };
        } finally {
            setLoading(false);
        }
    };

    const loginWithGoogle = async () => {
        try {
            setLoading(true);
            await signInWithPopup(auth, googleProvider);
            return { success: true };
        } catch (error) {
            console.error('Google login failed:', error);
            return { 
                success: false, 
                error: 'Google login failed. Please try again.'
            };
        } finally {
            setLoading(false);
        }
    };

    const loginWithMicrosoft = async () => {
        try {
            setLoading(true);
            await signInWithPopup(auth, microsoftProvider);
            return { success: true };
        } catch (error) {
            console.error('Microsoft login failed:', error);
            return {
                success: false, 
                error: 'Microsoft login failed. Please try again.'
            };
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const resetPassword = async (email) => {
        try {
            await sendPasswordResetEmail(auth, email);
            return {
                success: true, 
                message: 'Password reset email sent! Check your inbox'
            };
        } catch (error) {
            console.error('Password reset failed:', error);
            return {
                success: false, 
                error: getAuthErrorMessage(error.code)
            };
        }
    };

    const getAuthErrorMessage = (errorCode) => {
        switch (errorCode) {
            case 'auth/user-not-found':
                return 'No account found with this email address.';
            case 'auth/wrong-password':
                return 'Incorrect password.';
            case 'auth/email-already-in-use':
                return 'An account with this email already exists.';
            case 'auth/weak-password':
                return 'Password should be at least 6 characters.';
            case 'auth/invalid-email':
                return 'Invalid email address.';
            case 'auth/too-many-requests':
                return 'Too many failed attempts. Please try again later.';
            default:
                return 'An error occurred. Please try again.';
        }
    };

    // For backward compatibility
    const login = loginWithEmail;
    const register = registerWithEmail;

    const value = {
        user,
        loading,
        login,
        register,
        loginWithEmail,
        registerWithEmail,
        loginWithGoogle,
        loginWithMicrosoft,
        logout,
        resetPassword
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};